function collapseWhitespace(text) {
  return String(text || "").replace(/\s+/g, " ").trim();
}

function compactChinese(text) {
  return String(text || "").replace(/\s+/g, "").trim();
}

function uniqueItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    const normalized = String(item || "").trim().toLowerCase();
    if (!normalized || seen.has(normalized)) {
      return false;
    }
    seen.add(normalized);
    return true;
  });
}

function removeEnglishFillers(text) {
  return collapseWhitespace(
    String(text || "")
      .replace(/\b(um+|uh+|erm|ah|like|you know|i mean|sort of|kind of|basically|actually)\b/gi, " ")
      .replace(/\b(okay|ok|well|right)\b/gi, " ")
      .replace(/\b(the main thing is|mainly|pretty much)\b/gi, " ")
      .replace(/\s+,/g, ",")
      .replace(/\s+\./g, ".")
      .replace(/\s+\?/g, "?")
  );
}

function inferEnglishEntity(text) {
  const normalized = collapseWhitespace(text).toLowerCase();

  if (/\bgrandma\b/.test(normalized)) return "Grandma";
  if (/\bgrandpa\b/.test(normalized)) return "Grandpa";
  if (/\bmom\b|\bmother\b/.test(normalized)) return "Mom";
  if (/\bdad\b|\bfather\b/.test(normalized)) return "Dad";
  if (/\bteacher\b/.test(normalized)) return "The teacher";

  return "";
}

function normalizeEnglishClause(clause, lastEntity) {
  let nextClause = collapseWhitespace(clause)
    .replace(/^(and|then|also|so|but|because|after that|later|finally)\s+/i, "")
    .replace(/^(okay|ok|well|right)\s+/i, "")
    .replace(/^(maybe)\s+/i, "")
    .replace(/^(the main thing is)\s+/i, "")
    .replace(/^(she|he|they)\s+said\s+(that\s+)?(she|he|they)\s+/i, "$3 ")
    .replace(/^(she|he|they)\s+said\s+(that\s+)?/i, "$1 ")
    .replace(/\b(and then|after that)\b/gi, "then");

  if (lastEntity) {
    nextClause = nextClause.replace(/^(she|he|they)\b/i, lastEntity);
  }

  return nextClause.replace(/\b(and|or|but)\s*$/i, "").trim();
}

function splitEnglishSentences(text) {
  const normalized = collapseWhitespace(text);

  if (!normalized) {
    return [];
  }

  const expanded = normalized
    .replace(/[;]+/g, ". ")
    .replace(/\band also\b/gi, ". also")
    .replace(/,\s*(?=(and then|then|also|after that|later|tomorrow|next|finally)\b)/gi, ". ")
    .replace(/\band (?=(i|we|he|she|they|grandma|grandpa|mom|dad|teacher)\b)/gi, ". ")
    .replace(/\band (?=(maybe )?(check|confirm|bring|call|send|remind|remember|follow up)\b)/gi, ". ")
    .replace(/\b(and )(?=(i need to|we need to|i should|we should|remember to|remind\b|send\b|check\b|confirm\b|bring\b|call\b))/gi, ". ");

  const directSplit = expanded
    .split(/(?<=[.!?])\s+|\n+/)
    .map((segment) => collapseWhitespace(segment))
    .filter(Boolean);

  const segmentsToSplit = directSplit.length ? directSplit : [normalized];
  const finalSegments = [];
  let lastEntity = "";

  segmentsToSplit.forEach((segment) => {
    const hasConnector = /\b(and then|then|but|so|because|after that|finally|also)\b/i.test(segment);

    if (segment.length < 90 && !hasConnector) {
      const normalizedSegment = normalizeEnglishClause(segment, lastEntity);
      if (normalizedSegment) {
        finalSegments.push(normalizedSegment);
        lastEntity = inferEnglishEntity(normalizedSegment) || lastEntity;
      }
      return;
    }

    const clauseSplit = segment
      .replace(/\b(and then|then|but|so|because|after that|finally|also)\b/gi, " | ")
      .split("|")
      .map((part) => collapseWhitespace(part))
      .filter(Boolean);

    if (clauseSplit.length > 1) {
      clauseSplit.forEach((part) => {
        const normalizedPart = normalizeEnglishClause(part, lastEntity);
        if (!normalizedPart) {
          return;
        }
        finalSegments.push(normalizedPart);
        lastEntity = inferEnglishEntity(normalizedPart) || lastEntity;
      });
      return;
    }

    const normalizedSegment = normalizeEnglishClause(segment, lastEntity);
    if (normalizedSegment) {
      finalSegments.push(normalizedSegment);
      lastEntity = inferEnglishEntity(normalizedSegment) || lastEntity;
    }
  });

  return uniqueItems(finalSegments.filter(Boolean));
}

function punctuateEnglishSentence(sentence) {
  const trimmed = collapseWhitespace(sentence).replace(/\s+([,.!?])/g, "$1");
  if (!trimmed) {
    return "";
  }

  const capitalized = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capitalized) ? capitalized : `${capitalized}.`;
}

function cleanEnglishText(text) {
  const cleaned = removeEnglishFillers(text);
  const sentences = splitEnglishSentences(cleaned).map(punctuateEnglishSentence);
  return sentences.join(" ").trim();
}

function removeChineseFillers(text) {
  return compactChinese(
    String(text || "")
      .replace(/(嗯+|呃+|啊+|那个|这个|就是|其实|怎么说呢|然后呢|你知道吧|就是说)/g, "")
      .replace(/^(今天主要是|主要是|主要)/g, "")
  )
    .replace(/[，,]{2,}/g, "，")
    .replace(/[。]{2,}/g, "。")
    .trim();
}

function inferChineseEntity(text) {
  const normalized = compactChinese(text);

  if (normalized.includes("奶奶")) return "奶奶";
  if (normalized.includes("爷爷")) return "爷爷";
  if (normalized.includes("妈妈")) return "妈妈";
  if (normalized.includes("爸爸")) return "爸爸";
  if (normalized.includes("老师")) return "老师";

  return "";
}

function normalizeChineseClause(clause, lastEntity) {
  let nextClause = compactChinese(clause)
    .replace(/^(然后|另外|还有|而且|所以|后来)/, "")
    .replace(/^(今天主要是|主要是|主要)/, "")
    .replace(/^(她|他|他们)说/, "$1");

  if (lastEntity) {
    nextClause = nextClause.replace(/^(她|他|他们)/, lastEntity);
  }

  return nextClause.replace(/[，,]+$/g, "");
}

function splitChineseSentences(text) {
  const normalized = compactChinese(text);

  if (!normalized) {
    return [];
  }

  const expanded = normalized
    .replace(/[；;]/g, "。")
    .replace(/[，,](?=(然后|另外|还有|而且|所以|明天|今天|晚饭后|之后|接着|提醒|需要|记得|请|她想|他想|奶奶想|爷爷想|妈妈想|爸爸想|还要|还得|我晚饭后|我明天|我要|我需要))/g, "。");

  const directSplit = expanded
    .split(/[。！？；\n]+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  const segmentsToSplit = directSplit.length ? directSplit : [normalized];
  const finalSegments = [];
  let lastEntity = "";

  segmentsToSplit.forEach((segment) => {
    const hasConnector = /(然后|但是|不过|所以|因为|后来|最后|而且|同时)/.test(segment);

    if (segment.length < 28 && !hasConnector) {
      const normalizedSegment = normalizeChineseClause(segment, lastEntity);
      if (normalizedSegment) {
        finalSegments.push(normalizedSegment);
        lastEntity = inferChineseEntity(normalizedSegment) || lastEntity;
      }
      return;
    }

    const clauseSplit = segment
      .replace(/(然后|但是|不过|所以|因为|后来|最后|另外|还有|而且|明天|晚饭后|提醒|需要|记得|请|还要|还得|我要|我需要|我晚饭后|我明天)/g, "｜$1")
      .split("｜")
      .map((part) => part.trim())
      .filter(Boolean);

    if (clauseSplit.length > 1) {
      clauseSplit.forEach((part) => {
        const normalizedPart = normalizeChineseClause(part, lastEntity);
        if (!normalizedPart) {
          return;
        }
        finalSegments.push(normalizedPart);
        lastEntity = inferChineseEntity(normalizedPart) || lastEntity;
      });
      return;
    }

    const normalizedSegment = normalizeChineseClause(segment, lastEntity);
    if (normalizedSegment) {
      finalSegments.push(normalizedSegment);
      lastEntity = inferChineseEntity(normalizedSegment) || lastEntity;
    }
  });

  return uniqueItems(finalSegments.filter(Boolean));
}

function punctuateChineseSentence(sentence) {
  const trimmed = String(sentence || "").replace(/\s+/g, "").trim();
  if (!trimmed) {
    return "";
  }

  return /[。！？]$/.test(trimmed) ? trimmed : `${trimmed}。`;
}

function cleanChineseText(text) {
  const cleaned = removeChineseFillers(text);
  const sentences = splitChineseSentences(cleaned).map(punctuateChineseSentence);
  return sentences.join("").trim();
}

function splitSentencesByLanguage(cleanText, sourceLanguage) {
  return sourceLanguage === "zh"
    ? splitChineseSentences(cleanText)
    : splitEnglishSentences(cleanText);
}

function cleanByLanguage(rawTranscript, sourceLanguage) {
  return sourceLanguage === "zh"
    ? cleanChineseText(rawTranscript)
    : cleanEnglishText(rawTranscript);
}

function isActionSentence(sentence, sourceLanguage) {
  const pattern =
    sourceLanguage === "zh"
      ? /(需要|记得|提醒|安排|确认|跟进|发送|发给|检查|准备|带上?|打电话|联系|回复|提交|确认一下|请|要带|要发|要给|要去|要跟)/
      : /(need to|should|must|follow up|send|check|confirm|prepare|review|remember to|please|bring|call|remind)/i;

  return pattern.test(sentence);
}

function buildStructuredNotes(sentences, sourceLanguage) {
  if (!sentences.length) {
    return sourceLanguage === "zh" ? "主要内容\n- 暂未识别到清晰内容" : "Main Notes\n- No clear points detected";
  }

  const keySentences = sentences.filter((sentence) => !isActionSentence(sentence, sourceLanguage));
  const actionSentences = sentences.filter((sentence) => isActionSentence(sentence, sourceLanguage));
  const sections = [];

  if (keySentences.length) {
    sections.push(
      sourceLanguage === "zh" ? "主要内容" : "Main Notes",
      ...keySentences.map((item) => `- ${item}`)
    );
  }

  if (actionSentences.length) {
    if (sections.length) {
      sections.push("");
    }
    sections.push(
      sourceLanguage === "zh" ? "待办事项" : "Action Items",
      ...actionSentences.map((item) => `- ${item}`)
    );
  }

  if (!sections.length) {
    return sentences.map((item) => `- ${item}`).join("\n");
  }

  return sections.join("\n");
}

function scoreSentenceForTitle(sentence, sourceLanguage) {
  const normalized = String(sentence || "").trim();

  if (!normalized) {
    return -Infinity;
  }

  let score = normalized.length;

  if (isActionSentence(normalized, sourceLanguage)) {
    score -= 6;
  }

  if (sourceLanguage === "zh") {
    if (/(奶奶|爷爷|妈妈|爸爸|老师|学校|项目|照片|作业|时间|电话)/.test(normalized)) {
      score += 12;
    }
    if (/(聊天|通话)/.test(normalized)) {
      score -= 2;
    }
  } else {
    if (/\b(grandma|grandpa|mom|dad|teacher|school|project|photo|homework|time)\b/i.test(normalized)) {
      score += 12;
    }
    if (/\b(talked|called|spoke|chatted)\b/i.test(normalized)) {
      score -= 2;
    }
  }

  return score;
}

function buildTitle(sentences, sourceLanguage, keyPoints, actionItems) {
  const preferredCandidates = (keyPoints || []).length
    ? keyPoints
    : (actionItems || []).length
    ? actionItems
    : sentences || [];
  const candidates = [...preferredCandidates]
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  const best = candidates
    .slice()
    .sort((left, right) => scoreSentenceForTitle(right, sourceLanguage) - scoreSentenceForTitle(left, sourceLanguage))[0];

  if (!best) {
    return sourceLanguage === "zh" ? "未命名整理" : "Untitled Notes";
  }

  if (sourceLanguage === "zh") {
    return best
      .replace(/^(今天|刚才|刚刚)/, "")
      .replace(/[。！？]$/, "")
      .slice(0, 18);
  }

  const words = best
    .replace(/^(today|just now|okay|the main thing is)\s+/i, "")
    .replace(/[.!?]$/, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 8);
  return words.join(" ");
}

function buildSummary(sentences, sourceLanguage, actionItems) {
  if (!sentences.length) {
    return sourceLanguage === "zh"
      ? "还没有足够内容可以总结。"
      : "There is not enough content to summarize yet.";
  }

  const infoSentences = sentences.filter((sentence) => !isActionSentence(sentence, sourceLanguage));
  const summaryBase = (infoSentences.length ? infoSentences : sentences).slice(0, 2);

  if (sourceLanguage === "zh") {
    const summaryText = summaryBase
      .map((item) => punctuateChineseSentence(item))
      .join("");

    const extraAction = (actionItems || []).find((item) => !summaryBase.includes(item));

    if (extraAction) {
      return `${summaryText}接下来，${punctuateChineseSentence(extraAction).replace(/[。！？]$/, "")}。`;
    }

    return summaryText;
  }

  const summaryText = summaryBase
    .map((item) => punctuateEnglishSentence(item))
    .join(" ");

  const extraAction = (actionItems || []).find((item) => !summaryBase.includes(item));

  if (extraAction) {
    return `${summaryText} Next: ${punctuateEnglishSentence(extraAction)}`;
  }

  return summaryText;
}

function buildKeyPoints(sentences, sourceLanguage) {
  const informational = sentences.filter((sentence) => !isActionSentence(sentence, sourceLanguage));
  return uniqueItems((informational.length ? informational : sentences)).slice(0, 5);
}

function buildActionItems(sentences, sourceLanguage) {
  return uniqueItems(sentences.filter((sentence) => isActionSentence(sentence, sourceLanguage))).slice(0, 5);
}

export function freeOrganizeText(rawTranscript, sourceLanguage = "en") {
  const normalizedSource = sourceLanguage === "zh" ? "zh" : "en";
  const rawValue = String(rawTranscript || "").trim();
  const cleanText = cleanByLanguage(rawValue, normalizedSource);
  const sentences = splitSentencesByLanguage(cleanText, normalizedSource);
  const keyPoints = buildKeyPoints(sentences, normalizedSource);
  const actionItems = buildActionItems(sentences, normalizedSource);

  return {
    mode: "local",
    provider: "free-local",
    sourceLanguage: normalizedSource,
    rawTranscript: rawValue,
    cleanText,
    structuredNotes: buildStructuredNotes(sentences, normalizedSource),
    translatedText: "",
    title: buildTitle(sentences, normalizedSource, keyPoints, actionItems),
    summary: buildSummary(sentences, normalizedSource, actionItems),
    keyPoints,
    actionItems
  };
}

export default freeOrganizeText;
